"use client"

import { ReactNode, useMemo } from "react"
import { StorefrontBridgeProvider } from "@tapcart/storefront-bridge"
import * as Sentry from "@sentry/nextjs"

/**
 * Client-side wrapper for web mode (App Studio on a Shopify storefront).
 *
 * Exists because the root layout is a server component: it can pass
 * serializable config to a client provider, but not functions. The
 * storefront-bridge package deliberately has no Sentry dependency of its
 * own — it exposes a `config.onError` hook instead — so this wrapper is
 * where the hook gets bound to the app's Sentry client.
 *
 * Unexpected bridge errors (App Proxy failures, Shopify AJAX cart
 * failures) land in Sentry tagged with the action that failed. Expected
 * fallbacks (guest → localStorage) are not reported by the bridge.
 */
export default function WebModeBridge({
  appId,
  children,
}: {
  appId: string
  children: ReactNode
}) {
  const config = useMemo(
    () => ({
      appId,
      customer: null,
      onError: (error: unknown, context: string) => {
        Sentry.captureException(error, {
          tags: { appId, context: `storefront_bridge:${context}` },
        })
      },
    }),
    [appId]
  )

  return (
    <StorefrontBridgeProvider config={config}>
      {children}
    </StorefrontBridgeProvider>
  )
}
